// Redaction for what a Feedback report carries off the page: its address and
// the console errors captured on it. Applied in the browser as errors are
// recorded (`./consoleCapture`) and again on the server as the Asana task is
// written (`./report`), so a token in a URL or a log line never reaches Asana
// even from a client that skipped the first pass. Pure and import-free: it
// ships in the boot bundle with the console capture.

const redacted = "[redacted]";

/** A query key that looks like it carries a credential. */
const sensitiveKey = /token|code|key|secret|password|auth|session|sig/i;

function redactSearchParams(params: URLSearchParams): string {
  const kept = new URLSearchParams();

  for (const [key, value] of params) {
    kept.append(key, sensitiveKey.test(key) ? redacted : value);
  }

  const search = kept.toString();
  return search ? `?${search}` : "";
}

/**
 * Origin + path, plus the query with credential-looking values replaced; the
 * hash is dropped (OAuth implicit flows put tokens there). Throws on a string
 * that isn't an absolute URL — callers pass validated http(s) URLs.
 */
export function redactFeedbackUrl(value: string): string {
  const url = new URL(value);
  return `${url.origin}${url.pathname}${redactSearchParams(url.searchParams)}`;
}

/** The same for a route ("/path?query#hash"), which has no origin. */
export function redactFeedbackRoute(value: string): string {
  const url = new URL(value, "http://route.invalid");
  return `${url.pathname}${redactSearchParams(url.searchParams)}`;
}

const bearer = /\b(Bearer|Basic|Token)\s+[A-Za-z0-9._~+/=-]{8,}/gi;
const jwt = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;
const sensitiveParam =
  /([?&;][A-Za-z0-9_.-]*(?:token|code|key|secret|password|auth|session|sig)[A-Za-z0-9_.-]*=)[^&#\s"'<>]*/gi;
const email = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/** Masks credentials and email addresses inside free text (a log line). */
export function redactFeedbackText(value: string): string {
  return value
    .replace(bearer, `$1 ${redacted}`)
    .replace(jwt, redacted)
    .replace(sensitiveParam, `$1${redacted}`)
    .replace(email, "[email]");
}
